import { useQuery } from 'react-query';
import { DevicesApi, DevicesTypes } from '@frontend/api-devices';
import { useTranslation } from '@frontend/i18n';
import { DropdownField, FormFieldActionTypes, SkeletonLoader, useForm } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { DialOptionInstruction } from '../types';
import { ReadOnlyInstructionField } from './read-only-instruction';
import { InstructionFieldStyles } from './styles';

export const ForwardDeviceInstructionFields = ({
  onChange,
  instructionState,
  locationId,
  isReadOnly,
  disabled = false,
}: {
  onChange: (payload: DialOptionInstruction) => void;
  instructionState: DialOptionInstruction;
  locationId: string;
  isReadOnly?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const getDeviceName = (device: DevicesTypes.DeviceModel) => {
    if (device.type === 'desk_phone' || device.type === 'softphone') {
      return device.sipProfile?.name || device.name;
    }
    return device.name;
  };

  const { data: devices = [], isLoading } = useQuery({
    queryKey: [...queryKeys.devicesWithAddresses(), locationId],
    queryFn: () => DevicesApi.listDevicesWithHeaders(locationId),
    select: (data) => {
      return data
        .sort((a, b) => a.sipDevice.name.localeCompare(b.sipDevice.name))
        .map((device) => ({
          value: device.sipDevice.ID,
          label: getDeviceName(device.sipDevice),
        }));
    },
    staleTime: 10000,
    enabled: !!locationId && !isReadOnly,
  });

  const form = useForm({
    fields: {
      deviceId: {
        type: 'dropdown',
        value: instructionState.id,
      },
    },
    fieldStateReducer: (state, action) => {
      // When any field is updated, extract all field values and notify the parent component
      if (action.type === FormFieldActionTypes.Update) {
        const payload = {
          ...instructionState,
          id: state.deviceId.value,
        };
        onChange(payload);
      }

      return state;
    },
  });

  if (isLoading) {
    return <SkeletonLoader height={40} width={225} />;
  }

  if (isReadOnly) {
    return (
      <ReadOnlyInstructionField
        instructionName={instructionState.name ?? t('Unknown Device')}
        iconName={'cell-phone'}
        instructionType={t('Device')}
      />
    );
  }

  return (
    <DropdownField
      {...form.getFieldProps('deviceId')}
      name={'deviceId'}
      label={t('Device')}
      css={InstructionFieldStyles}
      disabled={disabled}
    >
      {devices?.map((device) => (
        <DropdownField.Option key={device.value} value={device.value}>
          {device.label}
        </DropdownField.Option>
      ))}
    </DropdownField>
  );
};
